<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Add new'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-fuel-expenses' }">{{
            $t("MENU.ITEM.EXP.FUELS_EXPENSES")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.ADD_NEW") }}</small>
          <span v-if="aiInvoiceId" class="badge badge-success ml-2">
            {{ $t("LABELS.FROM_AI_INVOICE") }}
          </span>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingFuelExpense"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
      <template v-slot:body>
        <AiInvoiceReferenceCard
          v-if="aiInvoiceData && !isLoadingAiInvoice"
          :aiInvoiceData="aiInvoiceData"
          class="mb-5"
        />

        <v-skeleton-loader
          v-if="isLoadingAiInvoice"
          type="card"
          class="mb-5"
        ></v-skeleton-loader>

        <FuelExpensesForm
          ref="FuelExpensesForm"
          :form="form"
          @add-row="handleAddRow"
        ></FuelExpensesForm>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingFuelExpense"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { SAVE_FUEL_EXPENSE } from "@/modules/fuel-expenses/store/fuelexpenses.module";
import { mapGetters } from "vuex";

import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import KTCard from "@/view/content/Card.vue";
import FuelExpensesForm from "@/modules/fuel-expenses/components/FuelExpensesForm.vue";
import AiInvoiceReferenceCard from "@/modules/ai-invoice-type-adapters/components/AiInvoiceReferenceCard.vue";

import { aiInvoiceFormMixin } from "@/modules/ai-invoice-type-adapters/mixins/aiInvoiceFormMixin.js";
import { fuelExpenseAiMixin } from "@/modules/ai-invoice-type-adapters/mixins/fuelExpenseAiMixin.js";

export default {
  name: "FuelExpensesAdd",
  mixins: [aiInvoiceFormMixin, fuelExpenseAiMixin],
  components: {
    KTCard,
    SaveButtonDropdown,
    FuelExpensesForm,
    AiInvoiceReferenceCard
  },
  data() {
    return {
      form: {
        gasTankStationId: null,
        invoiceNumber: null,
        invoiceDate: null,
        qty: 1,
        totalNoVat: "0.00",
        totalWithVat: "0.00",
        vat: "0.00",
        toAccounting: true,
        rows: []
      }
    };
  },
  computed: {
    ...mapGetters(["isLoadingFuelExpense"])
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.FUELS_EXPENSES"),
        route: { name: "list-fuel-expenses" }
      },
      { title: this.$i18n.t("LABELS.ADD_NEW") }
    ]);

    await this.loadFuelDependencies();

    if (this.aiInvoiceId) {
      await this.loadAiInvoiceData();
    }
  },
  methods: {
    prefillFromAiInvoice() {
      if (!this.aiInvoiceData) return;

      this.prefillBasicInvoiceInfo(this.form);
      this.autoMatchGasStation(this.form);
      this.autoPopulateFuelRows(this.form);
    },

    handleAddRow() {
      this.onSave("add-row");
    },

    onSave(next) {
      let vm = this;

      this.$refs.FuelExpensesForm.$v.form.$touch();
      if (this.$refs.FuelExpensesForm.$v.form.$anyError) {
        const errorMessages = [];

        if (this.$refs.FuelExpensesForm.$v.form.gasTankStationId.$error) {
          errorMessages.push(this.$t("MENU.ITEM.TANKS.GAS_STATION"));
        }
        if (this.$refs.FuelExpensesForm.$v.form.invoiceNumber.$error) {
          errorMessages.push(this.$t("FORM_LABELS.INVOICE_NUMBER"));
        }
        if (this.$refs.FuelExpensesForm.$v.form.invoiceDate.$error) {
          errorMessages.push(this.$t("FORM_LABELS.DATE"));
        }

        const errorText =
          errorMessages.length > 0
            ? `${this.$t(
                "ALERTS.MISSING_REQUIRED_FIELDS"
              )}: ${errorMessages.join(", ")}`
            : this.$t("ALERTS.FILL_REQUIRED_FIELDS");

        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      if (!this.$refs.FuelExpensesForm.validateRows()) {
        const errorText =
          this.$t("ALERTS.TANK_CHARGE_REQUIRED") ||
          "Each fuel row must have at least one tank charge";
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      // Add AI invoice reference if exists
      const saveData = { ...this.form };
      if (this.aiInvoiceId) {
        saveData.aiInvoiceId = this.aiInvoiceId;
      }

      this.$store
        .dispatch(SAVE_FUEL_EXPENSE, saveData)
        .then(data => {
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          if (next == "continue") {
            this.$router.push({
              name: "edit-fuel-expenses",
              params: { id: data.data.id }
            });
          }
          if (next == "add-row") {
            this.$router.push({
              name: "add-fuel-expenses-row",
              params: { expenseId: data.data.id }
            });
          }
          if (next == "new") {
            this.$router.push({ name: "add-fuel-expenses" });
          }
          if (next == "exit") {
            this.$router.push({ name: "list-fuel-expenses" });
          }
        })
        .catch(response => {
          let errorText = response.data.message;
          if (response.data && response.data.errors) {
            const formattedErrors = this.$errors.format(response.data.errors);
            if (formattedErrors.length > 0) {
              errorText = formattedErrors.join(", ");
            }
          }

          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: errorText
          });
        });
    }
  }
};
</script>

<style scoped>
.bg-light-success {
  background-color: #c9f7f5;
}

.bg-light {
  background-color: #f3f6f9;
}
</style>
